import { Label } from "layout/typography/Label";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { useContext, useEffect, useState } from "react";
import styled from "styled-components";

import { ToastContext } from "../../../context/ToastContext";
import {
  useCreateOverrideItemMutation,
  useUpdateOverrideItemMutation,
} from "../../../store/queries/baseRateFiles";
import {
  BaseRateOverrideRuleType,
  FeeScheduleType,
  IBaseRateOverrideItem,
} from "../../../types/baseRateFile";
import {
  FEE_SCHEDULE_TYPE_OPTIONS,
  RULE_TYPE_OPTIONS,
  SOURCE_OVERRIDE_OPTIONS,
} from "../utils/constants";

const StyledDialog = styled(Dialog)`
  .p-dialog-content {
    padding-bottom: 0;
  }
`;

interface IAddOverrideRuleDialogProps {
  visible: boolean;
  onHide: () => void;
  editItem?: IBaseRateOverrideItem | null;
}

function AddOverrideRuleDialog({
  visible,
  onHide,
  editItem,
}: IAddOverrideRuleDialogProps) {
  const toast = useContext(ToastContext);
  const [createOverrideItem, { isLoading: isCreating }] =
    useCreateOverrideItemMutation();
  const [updateOverrideItem, { isLoading: isUpdating }] =
    useUpdateOverrideItemMutation();

  const isEditMode = !!editItem;
  const isLoading = isCreating || isUpdating;

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [ruleType, setRuleType] = useState<BaseRateOverrideRuleType | null>(
    null
  );
  const [feeScheduleType, setFeeScheduleType] = useState<FeeScheduleType>(
    "HOSPITAL_OPPS_NATIONAL"
  );
  const [isEnabled, setIsEnabled] = useState(true);
  const [priority, setPriority] = useState<number | null>(100);
  const [hcpcs, setHcpcs] = useState("");
  const [mod, setMod] = useState("");
  const [overrideRate, setOverrideRate] = useState<number | null>(null);
  const [sourceOverride, setSourceOverride] = useState<string | null>(null);
  const [dmeposPriceColumn, setDmeposPriceColumn] = useState("");
  const [swapWithHcpcs, setSwapWithHcpcs] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (editItem) {
      setName(editItem.name);
      setDescription(editItem.description || "");
      setRuleType(editItem.ruleType);
      setFeeScheduleType(editItem.feeScheduleType || "HOSPITAL_OPPS_NATIONAL");
      setIsEnabled(editItem.isEnabled);
      setPriority(editItem.priority);
      setHcpcs(editItem.hcpcs);
      setMod(editItem.mod || "");
      setOverrideRate(
        editItem.overrideRate ? Number(editItem.overrideRate) : null
      );
      setSourceOverride(editItem.sourceOverride || null);
      setDmeposPriceColumn(editItem.dmeposPriceColumn || "");
      setSwapWithHcpcs(editItem.swapWithHcpcs || "");
      setNotes(editItem.notes || "");
    }
  }, [editItem]);

  const resetForm = () => {
    setName("");
    setDescription("");
    setRuleType(null);
    setFeeScheduleType("HOSPITAL_OPPS_NATIONAL");
    setIsEnabled(true);
    setPriority(100);
    setHcpcs("");
    setMod("");
    setOverrideRate(null);
    setSourceOverride(null);
    setDmeposPriceColumn("");
    setSwapWithHcpcs("");
    setNotes("");
  };

  const handleClose = () => {
    resetForm();
    onHide();
  };

  const isFormValid =
    name.trim() &&
    ruleType &&
    hcpcs.trim() &&
    priority !== null &&
    (ruleType !== "FIXED_RATE" || overrideRate !== null) &&
    (ruleType !== "SOURCE_OVERRIDE" || sourceOverride) &&
    (ruleType !== "SWAP" || swapWithHcpcs.trim());

  const handleSubmit = async () => {
    if (!isFormValid || !ruleType || priority === null) {
      return;
    }

    const payload = {
      name: name.trim(),
      description: description.trim() || undefined,
      ruleType,
      feeScheduleType,
      isEnabled,
      priority,
      hcpcs: hcpcs.trim(),
      mod: mod.trim() || undefined,
      overrideRate:
        ruleType === "FIXED_RATE" && overrideRate !== null
          ? overrideRate
          : undefined,
      sourceOverride:
        ruleType === "SOURCE_OVERRIDE"
          ? sourceOverride ?? undefined
          : undefined,
      dmeposPriceColumn:
        ruleType === "SOURCE_OVERRIDE" && sourceOverride === "DMEPOS"
          ? dmeposPriceColumn.trim() || undefined
          : undefined,
      swapWithHcpcs: ruleType === "SWAP" ? swapWithHcpcs.trim() : undefined,
      notes: notes.trim() || undefined,
    };

    try {
      if (isEditMode && editItem) {
        await updateOverrideItem({ id: editItem.id, data: payload }).unwrap();
        toast?.current?.show({
          severity: "success",
          summary: "Success",
          detail: "Override rule updated successfully",
        });
      } else {
        await createOverrideItem(payload).unwrap();
        toast?.current?.show({
          severity: "success",
          summary: "Success",
          detail: "Override rule created successfully",
        });
      }

      handleClose();
    } catch (error) {
      toast?.current?.show({
        severity: "error",
        summary: "Error",
        detail: `Failed to ${isEditMode ? "update" : "create"} override rule`,
      });
    }
  };

  return (
    <StyledDialog
      header={isEditMode ? "Edit Rule" : "Add Rule"}
      visible={visible}
      onHide={handleClose}
      style={{ width: "600px" }}
    >
      <div className="p-fluid">
        <div className="field">
          <Label htmlFor="ruleName">Name *</Label>
          <InputText
            id="ruleName"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter a name for this rule"
            className="w-full"
          />
        </div>

        <div className="field">
          <Label htmlFor="ruleDescription">Description</Label>
          <InputText
            id="ruleDescription"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter a description"
            className="w-full"
          />
        </div>
        <div className="field">
          <Label htmlFor="feeScheduleType">Fee Schedule Type *</Label>
          <Dropdown
            id="feeScheduleType"
            value={feeScheduleType}
            options={FEE_SCHEDULE_TYPE_OPTIONS}
            onChange={(e) => setFeeScheduleType(e.value)}
            placeholder="Select fee schedule type"
            className="w-full"
          />
        </div>

        <div className="grid">
          <div className="col-6">
            <div className="field">
              <Label htmlFor="ruleType">Rule Type *</Label>
              <Dropdown
                id="ruleType"
                value={ruleType}
                options={RULE_TYPE_OPTIONS}
                onChange={(e) => setRuleType(e.value)}
                placeholder="Select rule type"
                className="w-full"
              />
            </div>
          </div>
          <div className="col-6">
            <div className="field">
              <Label htmlFor="priority">Priority *</Label>
              <InputNumber
                id="priority"
                value={priority}
                onValueChange={(e) => setPriority(e.value ?? null)}
                placeholder="e.g., 100"
                className="w-full"
                min={1}
              />
            </div>
          </div>
        </div>

        <div className="grid">
          <div className="col-6">
            <div className="field">
              <Label htmlFor="hcpcs">HCPCS Code *</Label>
              <InputText
                id="hcpcs"
                value={hcpcs}
                onChange={(e) => setHcpcs(e.target.value.toUpperCase())}
                placeholder="e.g., 73222"
                className="w-full"
              />
            </div>
          </div>
          <div className="col-6">
            <div className="field">
              <Label htmlFor="mod">Modifier</Label>
              <InputText
                id="mod"
                value={mod}
                onChange={(e) => setMod(e.target.value.toUpperCase())}
                placeholder="e.g., TC, 26"
                className="w-full"
              />
            </div>
          </div>
        </div>

        {ruleType === "FIXED_RATE" && (
          <div className="field">
            <Label htmlFor="overrideRate">Override Rate ($) *</Label>
            <InputNumber
              id="overrideRate"
              value={overrideRate}
              onValueChange={(e) => setOverrideRate(e.value ?? null)}
              placeholder="Enter fixed rate"
              className="w-full"
              mode="currency"
              currency="USD"
              minFractionDigits={2}
            />
          </div>
        )}

        {ruleType === "SOURCE_OVERRIDE" && (
          <div className="field">
            <Label htmlFor="sourceOverride">Source Override *</Label>
            <Dropdown
              id="sourceOverride"
              value={sourceOverride}
              options={SOURCE_OVERRIDE_OPTIONS}
              onChange={(e) => setSourceOverride(e.value)}
              placeholder="Select source"
              className="w-full"
            />
          </div>
        )}

        {ruleType === "SOURCE_OVERRIDE" && sourceOverride === "DMEPOS" && (
          <div className="field">
            <Label htmlFor="dmeposPriceColumn">DMEPOS Price Column</Label>
            <InputText
              id="dmeposPriceColumn"
              value={dmeposPriceColumn}
              onChange={(e) => setDmeposPriceColumn(e.target.value)}
              placeholder="e.g., ceiling, floor, TN_NR"
              className="w-full"
            />
          </div>
        )}

        {ruleType === "SWAP" && (
          <div className="field">
            <Label htmlFor="swapWithHcpcs">Swap With HCPCS *</Label>
            <InputText
              id="swapWithHcpcs"
              value={swapWithHcpcs}
              onChange={(e) => setSwapWithHcpcs(e.target.value.toUpperCase())}
              placeholder="Enter HCPCS code to swap with"
              className="w-full"
            />
          </div>
        )}

        <div className="field">
          <Label htmlFor="notes">Notes</Label>
          <InputTextarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Additional notes"
            className="w-full"
            rows={3}
          />
        </div>

        <div className="field flex align-items-center gap-2">
          <InputSwitch
            id="isEnabled"
            checked={isEnabled}
            onChange={(e) => setIsEnabled(e.value)}
          />
          <Label htmlFor="isEnabled" className="mb-0">
            Enabled
          </Label>
        </div>

        <div className="flex justify-content-end gap-2 mt-4 pb-3">
          <Button
            label={isEditMode ? "Update" : "Create"}
            icon="pi pi-check"
            onClick={handleSubmit}
            disabled={!isFormValid}
            loading={isLoading}
          />
          <Button
            label="Cancel"
            icon="pi pi-times"
            className="p-button-outlined-light"
            onClick={handleClose}
          />
        </div>
      </div>
    </StyledDialog>
  );
}

export default AddOverrideRuleDialog;
